// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { collectionGroup, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';
import { db } from '../firebase';
import { MediaItem, getThumbnail } from '../types';

interface Category {
    id: string;
    name: string;
    label: string;
}

// Feed categories matching pinterest-catalog-feed.ts
const FEED_CATEGORIES: Record<string, { $key: string; label: string; value: string; name: string }> = {
    new: { $key: "4Yxrp4Bi36KQGjZdAREV", label: "New > Trends", value: "new-greater-trends", name: "new-greater-trends" },
    dresses: { $key: "CWKFedKGs8DPVEnA3Bea", label: "Dresses", value: "women-greater-clothing-greater-dresses", name: "women-greater-clothing-greater-dresses" },
    bubu: { $key: "65PHtNkQgi8HgMIeIlaX", label: "Khaftan & Bubu", value: "women-greater-clothing-greater-dresses-greater-khaftan-and-bubu-dresses", name: "women-greater-clothing-greater-dresses-greater-khaftan-and-bubu-dresses" },
    gowns: { $key: "08f8B709fmwQoaCuf2A8", label: "African Gowns", value: "women-greater-clothing-greater-dresses-greater-african-gowns", name: "women-greater-clothing-greater-dresses-greater-african-gowns" },
    tops: { $key: "TJGAZdm99edRo4XWB3rG", label: "Tops", value: "women-greater-clothing-greater-tops", name: "women-greater-clothing-greater-tops" },
    matching_set: { $key: "9dtcA79cgLnATxk4nbow", label: "Matching Sets", value: "women-greater-clothing-greater-matching-sets", name: "women-greater-clothing-greater-matching-sets" },
};

const PinterestPostingPage: React.FC = () => {
    const [products, setProducts] = useState<MediaItem[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('new');
    const [filteredProducts, setFilteredProducts] = useState<MediaItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [showPosted, setShowPosted] = useState(false);

    // Load products using collectionGroup (like pinterest-catalog-feed.ts)
    useEffect(() => {
        const q = query(
            collectionGroup(db, 'products'),
            where('shop.isIndexible', '==', true),
            orderBy('createdAt', 'desc'),
            limit(500)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedProducts: MediaItem[] = [];
            const categoryMap = new Map<string, number>();

            snapshot.forEach((docSnap) => {
                const data = docSnap.data();

                // Skip items hidden from search engines or with "Type of outfit:" in description
                // (matching getPinterestCatalogProducts logic)
                if (data.shop?.isHiddenFromSearchEngines || data.description?.includes('Type of outfit:')) return;

                fetchedProducts.push({
                    id: docSnap.id,
                    $key: docSnap.id,
                    $shopkey: docSnap.ref.parent.parent?.path || '',
                    shopUsername: data.shop?.username || '',
                    ...data
                } as MediaItem);
            });

            setProducts(fetchedProducts);
            setFilteredProducts(fetchedProducts);

            // Build categories list from FEED_CATEGORIES
            const categoryList: Category[] = [
                { id: 'all', name: 'all', label: 'All Products' }
            ];
            Object.entries(FEED_CATEGORIES).forEach(([key, cat]) => {
                categoryList.push({
                    id: key,
                    name: cat.name,
                    label: cat.label
                });
            });
            setCategories(categoryList);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Filter products by category and rssFeedStatus
    useEffect(() => {
        let filtered = products;

        // Filter by category
        if (selectedCategory !== 'all') {
            const feedCategory = FEED_CATEGORIES[selectedCategory];
            if (feedCategory) {
                filtered = filtered.filter(p => {
                    const productCategories = (p as any).category || [];
                    if (Array.isArray(productCategories)) {
                        return productCategories.some((cat: any) =>
                            cat?.$key === feedCategory.$key ||
                            cat?.value === feedCategory.value ||
                            cat?.name === feedCategory.name
                        );
                    }
                    return false;
                });
            }
        }

        // Filter by posted status
        if (!showPosted) {
            filtered = filtered.filter(p => !(p as any).rssFeedPinterestStatus);
        }

        setFilteredProducts(filtered);
    }, [selectedCategory, products, showPosted]);

    // Count stats
    const postedCount = products.filter(p => (p as any).rssFeedPinterestStatus).length;
    const notPostedCount = products.length - postedCount;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Pinterest Feed</h1>
                <p className="text-gray-600 mt-1">Products available for Pinterest RSS feed posting</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-700">{products.length}</div>
                    <div className="text-sm text-gray-600">Total Products</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-700">{postedCount}</div>
                    <div className="text-sm text-green-600">Posted to Pinterest</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-700">{notPostedCount}</div>
                    <div className="text-sm text-yellow-600">Not Posted Yet</div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">Category:</label>
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                                {cat.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showPosted}
                            onChange={(e) => setShowPosted(e.target.checked)}
                            className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700">Show already posted</span>
                    </label>
                    <span className="text-sm text-gray-500">
                        Showing {filteredProducts.length} products
                    </span>
                </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredProducts.map((product) => {
                    const isPosted = !!(product as any).rssFeedPinterestStatus;

                    return (
                        <div
                            key={product.id}
                            className={`relative group rounded-xl overflow-hidden border-2 transition-all ${isPosted ? 'border-green-300' : 'border-transparent hover:border-gray-300'}`}
                        >
                            {/* Posted Badge */}
                            {isPosted && (
                                <div className="absolute top-2 right-2 z-10 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Posted
                                </div>
                            )}

                            {/* Product Image */}
                            <div className="aspect-[3/4] bg-gray-100">
                                <img
                                    src={getThumbnail(product, 'grid')}
                                    alt={product.name || 'Product'}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>

                            {/* Product Info */}
                            <div className="p-3 bg-white">
                                <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                                    {product.name || 'Unnamed Product'}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    {(product as any).shop?.username || 'Unknown shop'}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {filteredProducts.length === 0 && (
                <div className="text-center py-20">
                    <p className="text-gray-500">
                        {showPosted ? 'No products found in this category' : 'All products in this category have been posted'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default PinterestPostingPage;
